import { useMemo, useState } from "react";
import { baueVerlauf } from "../dienste/trinkverlauf.js";
import { FLASCHENGROESSEN } from "../kern/konstanten.js";
import { fmtEuro, jahrLabel } from "../kern/format.js";
import { locale, t } from "../i18n/index.js";
import { Btn, Auswahl, Eingabe, Leer, TextKnopf } from "../ui/primitive.jsx";

/* ================================================================
   Trinkverlauf — die Bewegungen des Kellers in zeitlicher Folge.

   Der Keller zeigt sonst immer nur den Jetztstand. Hier geht es um
   den Weg dorthin: Was kam wann herein, was ging heraus. Gruppiert
   wird nach Monaten, weil das die Einheit ist, in der man sich an
   den eigenen Konsum erinnert („im Mai war viel los").

   Neben den Ein- und Auslagerungen stehen die Kritiken: Wann eine
   Flasche getrunken wurde und was man von ihr hielt, gehört in
   dieselbe Chronik. In die Flaschensummen gehen sie nicht ein.

   Hier landen auch die Kritiken, die noch keinen Wein haben — im
   Glas notiert, Zuordnung später. Sie sind als solche gekennzeichnet
   und über einen eigenen Filter erreichbar, denn dieser Verlauf ist
   der einzige Ort, an dem sie auftauchen: Überall sonst hängen
   Kritiken an einem Wein, und einen haben sie noch nicht.

   Die Kopfzahlen beziehen sich stets auf die gefilterte Auswahl —
   sonst müsste man raten, worauf sich eine Summe bezieht.
   ================================================================ */

const ART_LABEL = {
  zugang: "Eingelagert",
  entnahme: "Entnommen",
  weinkarte: "Auf Weinkarte",
  kritik: "Bewertet",
};

const FILTER = [
  { key: "alle", label: "Alle" },
  { key: "zugang", label: "Einlagerungen" },
  { key: "entnahme", label: "Entnahmen" },
  { key: "weinkarte", label: "Weinkarte" },
  { key: "kritik", label: "Kritiken" },
  { key: "offen", label: "Nicht zugeordnet" },
];

/* „August 2026" in der eingestellten Sprache. Ohne Datum bleibt nur
   ein Sammeltitel — Altbestände aus Importen haben mitunter keinen
   Zeitstempel. */
function monatsTitel(schluessel) {
  if (!schluessel) return t("Ohne Datum");
  const [j, m] = schluessel.split("-");
  try {
    return new Date(Number(j), Number(m) - 1, 1)
      .toLocaleDateString(locale(), { month: "long", year: "numeric" });
  } catch (e) {
    return `${m}.${j}`;
  }
}

/* Tag ohne Jahr — das steht schon in der Monatsüberschrift. */
function tagTitel(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  try {
    return d.toLocaleDateString(locale(), { day: "2-digit", month: "2-digit" });
  } catch (e) {
    return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`;
  }
}

/* Vorzeichenbehaftete Zahl mit typografischem Minus — dasselbe
   Zeichen wie in den Bewegungszeilen, sonst steht in derselben
   Kopfzeile einmal „−4" und einmal „-2". */

const groesseKurz = (key) => {
  const g = FLASCHENGROESSEN.find((x) => x.key === key);
  return g ? g.kurz : key || "";
};

function Kennzahl({ label, wert, zusatz = null }) {
  return (
    <div className="wl-tv-kennzahl">
      <span className="wl-tv-kennwert num">{wert}</span>
      <span className="wl-tv-kennlabel">{label}</span>
      {zusatz && <span className="wl-tv-kennzusatz num">{zusatz}</span>}
    </div>
  );
}

function TrinkverlaufPanel({ db, geheZu = null, oeffneModal = null, onKritikErfassen = null }) {
  const [art, setArt] = useState("alle");
  const [jahr, setJahr] = useState("");
  const [suche, setSuche] = useState("");

  const verlauf = useMemo(
    () => baueVerlauf(db, { art, jahr: jahr === "" ? null : Number(jahr), suche }),
    [db, art, jahr, suche],
  );

  const { summe, monate, jahre, eintraege, gesamt } = verlauf;
  const gefiltert = art !== "alle" || jahr !== "" || suche.trim() !== "";

  /* Sprung zum Wein: Der Verlauf ist ein Einstieg in den Keller,
     kein Selbstzweck — aus einer Zeile heraus soll man beim Wein
     landen. Das Modal schließt der Aufrufer über geheZu. */
  const zumWein = (e) => {
    if (!geheZu || !e.weinId) return;
    geheZu(e.jgId
      ? { view: "jahrgang", weinId: e.weinId, jgId: e.jgId }
      : { view: "wein", id: e.weinId });
  };

  /* Kritik erfassen war ein eigener Menüpunkt. Hier steht es richtiger:
     Der Verlauf ist der Ort, an dem Kritiken erscheinen — und der
     einzige, an dem noch nicht zugeordnete auffindbar sind. */
  const ErfassenKnopf = () => (onKritikErfassen ? (
    <Btn variante="voll" onClick={onKritikErfassen}>{t("Kritik erfassen")}</Btn>
  ) : null);

  if (gesamt === 0) {
    return (
      <div className="wl-trinkverlauf">
        <Leer text={t("Noch nichts erfasst. Sobald du Flaschen einlagerst, entnimmst oder eine Kritik schreibst, entsteht hier der Verlauf.")} />
        <div className="wl-tv-erfassen"><ErfassenKnopf /></div>
      </div>
    );
  }

  return (
    <div className="wl-trinkverlauf">
      <div className="wl-tv-kennzahlen">
        <Kennzahl label={t("eingelagert")} wert={summe.ein > 0 ? `+${summe.ein}` : "0"}
          zusatz={summe.einWert > 0 ? fmtEuro(summe.einWert) : null} />
        <Kennzahl label={t("ausgelagert")} wert={summe.aus > 0 ? `\u2212${summe.aus}` : "0"}
          zusatz={summe.ausWert > 0 ? fmtEuro(summe.ausWert) : null} />
        {summe.kritiken > 0 && <Kennzahl label={t("Kritiken")} wert={summe.kritiken} />}
        {summe.offen > 0 && <Kennzahl label={t("Nicht zugeordnet")} wert={summe.offen} />}
      </div>

      <div className="wl-tv-erfassen"><ErfassenKnopf /></div>

      <div className="wl-tv-filter">
        {/* Als Auswahlliste statt als Knopfreihe: sechs Schaltflächen
            brauchten auf dem Telefon zwei Zeilen und drängten Jahr und
            Suche nach unten. Die Wahl ist ohnehin exklusiv — genau
            das, wofür eine Auswahlliste da ist. */}
        <div className="wl-tv-filterrest">
          <Auswahl value={art} onChange={(e) => setArt(e.target.value)}
            aria-label={t("Art der Bewegung")}>
            {FILTER.map((f) => <option key={f.key} value={f.key}>{t(f.label)}</option>)}
          </Auswahl>
          <Auswahl value={jahr} onChange={(e) => setJahr(e.target.value)}
            aria-label={t("Jahr")}>
            <option value="">{t("Alle Jahre")}</option>
            {jahre.map((j) => <option key={j} value={j}>{j}</option>)}
          </Auswahl>
          <Eingabe type="search" value={suche} placeholder={t("Wein, Winzer, Händler …")}
            aria-label={t("Suchen")} onChange={(e) => setSuche(e.target.value)} />
        </div>
      </div>

      {eintraege.length === 0 && (
        <Leer text={t("Zu dieser Auswahl gibt es keine Bewegungen.")}
          aktion={gefiltert
            ? <TextKnopf onClick={() => { setArt("alle"); setJahr(""); setSuche(""); }}>
                {t("Filter zurücksetzen")}
              </TextKnopf>
            : null} />
      )}

      {/* Die Zeilen haben feste Spalten; auf dem Telefon wird der
          Bereich waagerecht geschoben, statt den Inhalt zu quetschen. */}
      <div className="wl-tv-scroll">
        <div className="wl-tv-spur">
      {monate.map((m) => (
        <section className="wl-tv-monat" key={m.schluessel || "ohne"}>
          <header className="wl-tv-monatskopf">
            <h3>{monatsTitel(m.schluessel)}</h3>
            <span className="wl-tv-monatssumme num">
              {m.summe.ein > 0 && <span className="ist-ein">+{m.summe.ein}</span>}
              {m.summe.ein > 0 && m.summe.aus > 0 && " · "}
              {m.summe.aus > 0 && <span className="ist-aus">−{m.summe.aus}</span>}
            </span>
          </header>
          <ul className="wl-tv-liste">
            {m.eintraege.map((e) => (
              <li className="wl-tv-zeile" key={e.art + ":" + e.id}>
                {/* Ein geliehenes Datum wird als solches ausgewiesen —
                    sonst läse es sich wie eine erfasste Angabe. */}
                <span className={"wl-tv-tag num" + (e.datumGeliehen ? " ist-geliehen" : "")}
                  title={e.datumGeliehen ? t("Datum der zugehörigen Entnahme — die Kritik selbst ist undatiert") : undefined}>
                  {tagTitel(e.datum)}
                </span>
                <span className={"wl-histart wl-histart--" + e.art}>{t(ART_LABEL[e.art] || e.art)}</span>
                <span className="wl-tv-menge num">
                  {e.art === "kritik"
                    ? e.bewertung
                    : <>{e.art === "zugang" ? "+" : "−"}{e.menge} × {groesseKurz(e.groesse)}</>}
                </span>
                <span className={"wl-tv-wein" + (e.offen ? " ist-offen" : "")}>
                  {geheZu && e.weinId
                    ? <TextKnopf onClick={() => zumWein(e)}>{e.weinName || t("Unbekannter Wein")}</TextKnopf>
                    : <span>{e.weinName || (e.offen ? t("Ohne Angabe") : t("Unbekannter Wein"))}</span>}
                  <span className="wl-tv-zusatz">
                    {/* Ohne Wein gibt es weder Winzer noch Jahrgang —
                        stattdessen der Hinweis, woran es hier fehlt. */}
                    {e.offen && <span className="wl-tv-marke">{t("noch nicht zugeordnet")}</span>}
                    {!e.offen && e.winzerName}
                    {!e.offen && e.winzerName && " · "}
                    {!e.offen && `${t("Jg.")} ${jahrLabel(e.jahr)}`}
                    {e.haendler && ` · ${e.haendler}`}
                    {e.karteName && ` · ${e.karteName}`}
                    {e.kritiker && ` · ${e.kritiker}`}
                    {e.datumGeliehen && ` · ${t("Datum der Entnahme")}`}
                  </span>
                </span>
                <span className="wl-tv-wert num">{e.wert != null ? fmtEuro(e.wert) : ""}</span>
                {/* Weinkarten-Buchungen hängen an einem Karteneintrag und
                    werden dort geändert — hier wäre nur die Bewegung
                    korrigiert, die Karte liefe auseinander. */}
                {/* Eine offene Kritik führt zurück in die Zuordnung —
                    das ist hier die eigentlich fällige Handlung, nicht
                    das Bearbeiten der Bewertung. */}
                {oeffneModal && e.offen && (
                  <TextKnopf onClick={() => oeffneModal({
                    typ: "schnellkritik", objekt: e.kritik,
                    zurueck: { typ: "trinkverlauf" },
                  })}>
                    {t("Wein zuordnen")}
                  </TextKnopf>
                )}
                {oeffneModal && !e.offen && e.art !== "weinkarte" && (
                  <TextKnopf className="ist-dezent"
                    onClick={() => oeffneModal(e.art === "kritik"
                      ? {
                          typ: "kritik", objekt: e.kritik,
                          kontext: { weinId: e.weinId, jgId: e.jgId, jahr: e.jahr },
                          zurueck: { typ: "trinkverlauf" },
                        }
                      : {
                          typ: "bewegung", objekt: e.bewegung,
                          kontext: { weinId: e.weinId, jgId: e.jgId, jahr: e.jahr },
                          zurueck: { typ: "trinkverlauf" },
                        })}>
                    {t("Bearbeiten")}
                  </TextKnopf>
                )}
              </li>
            ))}
          </ul>
        </section>
      ))}
        </div>
      </div>
    </div>
  );
}

export { TrinkverlaufPanel };
